import React, { useEffect, useMemo, useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { IconBaseProps } from 'react-icons'
import {
  MdChevronRight as MdChevronRightRaw,
  MdAdd as MdAddRaw,
  MdMusicNote as MdMusicNoteRaw,
  MdArrowBack as MdArrowBackRaw,
  MdMoreHoriz as MdMoreHorizRaw,
} from 'react-icons/md'
import { notification, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'

import './Orders.scss'
import CreateOrderPlaylistView from '../../components/CreateOrderPlaylistView/CreateOrderPlaylistView'
// import SearchInput from '../../components/SearchInput/SearchInput'

import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetUserOrders, Order } from '../../utils/redux/ordersSlice'

type OrderStatus = 'processing' | 'delivered'

const MdChevronRight = MdChevronRightRaw as unknown as FC<IconBaseProps>
const MdAdd = MdAddRaw as unknown as FC<IconBaseProps>
const MdMusicNote = MdMusicNoteRaw as unknown as FC<IconBaseProps>
const MdArrowBack = MdArrowBackRaw as unknown as FC<IconBaseProps>
const MdMoreHoriz = MdMoreHorizRaw as unknown as FC<IconBaseProps>

function normalizeTs(ts: number) {
  return ts < 1e12 ? ts * 1000 : ts
}

function formatDate(ts: number, locale = 'en-GB') {
  const d = new Date(normalizeTs(ts))
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d)
}

function getOrderStatus(order: any): OrderStatus {
  const st = String(order?.status ?? '').toLowerCase()
  if (st === 'delivered') return 'delivered'
  if (st === 'processing') return 'processing'

  return Array.isArray(order?.playlists) && order.playlists.length > 0
    ? 'delivered'
    : 'processing'
}

function toLocaleFromLang(lang: string) {
  const code = String(lang || 'en').split('-')[0]
  if (code === 'uk') return 'uk-UA'
  if (code === 'fr') return 'fr-FR'
  if (code === 'ru') return 'ru-RU'
  return 'en-GB'
}

/** Безопасно достаём строковый id из любого значения */
function asId(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    const s = String(value).trim()
    return s || ''
  }
  return ''
}

/**
 * Пытаемся вытащить playlistId из разных возможных форматов order:
 * - order.playlistId
 * - order.playlist._id / order.playlist.id
 * - order.playlists[] (string | {_id|id})
 * - order.playlistIds[] (string | number)
 */
function extractPlaylistId(order: Record<string, any>): string | null {
  const directId =
    asId(order.playlistId) || asId(order.playlist_id) || asId(order.playlistID)
  if (directId) return directId

  if (order.playlist && typeof order.playlist === 'object') {
    const nestedId = asId(order.playlist._id) || asId(order.playlist.id)
    if (nestedId) return nestedId
  }

  if (Array.isArray(order.playlists)) {
    for (const p of order.playlists) {
      if (typeof p === 'string' || typeof p === 'number') {
        const id = asId(p)
        if (id) return id
      } else if (p && typeof p === 'object') {
        const id = asId(p._id) || asId(p.id)
        if (id) return id
      }
    }
  }

  if (Array.isArray(order.playlistIds)) {
    for (const pid of order.playlistIds) {
      const id = asId(pid)
      if (id) return id
    }
  }

  return null
}

export default function Orders() {
  const { t, i18n } = useTranslation('orders')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [searchValue, setSearchValue] = useState('')
  const [create, setCreate] = useState(false)

  const { allOrders, listLoading } = useAppSelector((s: any) => s.orders)

  const locale = useMemo(
    () => toLocaleFromLang(i18n.resolvedLanguage || i18n.language || 'en'),
    [i18n.language, i18n.resolvedLanguage],
  )

  const statusLabel: Record<OrderStatus, string> = useMemo(
    () => ({
      processing: t('status.processing', { defaultValue: 'Processing your order' }),
      delivered: t('status.delivered', { defaultValue: 'Order delivered' }),
    }),
    [t],
  )

  const fetchOrders = async () => {
    try {
      await dispatch(attemptGetUserOrders()).unwrap()
    } catch (e: any) {
      notification.error({
        title: 'Error',
        message: t('notifications.title', { defaultValue: 'Orders' }),
        description:
          e?.message ||
          t('errors.failedToLoad', { defaultValue: 'Failed to load orders' }),
        placement: 'topRight',
      })
    }
  }

  useEffect(() => {
    void dispatch(attemptGetUserOrders())
  }, [dispatch])

  const filteredOrders = useMemo(() => {
    const q = searchValue.trim().toLowerCase()
    const arr: Order[] = Array.isArray(allOrders) ? allOrders : []
    if (!q) return arr

    return arr.filter((o) => {
      const date = formatDate(o.createdAt, locale).toLowerCase()
      const business = String((o as any).businessName ?? '').toLowerCase()
      const status = getOrderStatus(o)
      return (
        date.includes(q) ||
        business.includes(q) ||
        statusLabel[status].toLowerCase().includes(q)
      )
    })
  }, [allOrders, searchValue, statusLabel, locale])

  const pending = useMemo(
    () => filteredOrders.filter((o) => getOrderStatus(o) === 'processing'),
    [filteredOrders],
  )

  const completed = useMemo(
    () => filteredOrders.filter((o) => getOrderStatus(o) === 'delivered'),
    [filteredOrders],
  )

  const onCreateOrder = () => setCreate(true)

  const onOpenOrder = (order: Order) => {
    const status = getOrderStatus(order)

    // По ТЗ: переход нужен для "Завершені замовлення"
    if (status === 'delivered') {
      const playlistId = extractPlaylistId(order as unknown as Record<string, any>)

      if (!playlistId) {
        notification.warning({
          title: 'Error',
          message: t('notifications.title', { defaultValue: 'Orders' }),
          description: t('errors.playlistNotFoundInOrder', {
            defaultValue: 'Playlist ID was not found in this order.',
          }),
          placement: 'topRight',
        })
        return
      }

      navigate(`/playlist/${playlistId}`)
      return
    }

    // Для processing пока оставляем без перехода
    console.log('open order:', order)
  }

  return (
    <div className="ordersPage">
      {create ? (
        <CreateOrderPlaylistView
          onBack={() => {
            setCreate(false)
            void fetchOrders()
          }}
        />
      ) : (
        <>
          <div className="ordersMobileBar">
            <button
              type="button"
              className="ordersMobileIconBtn"
              aria-label={t('a11y.back', { defaultValue: 'Back' })}
              onClick={() => navigate(-1)}
            >
              <MdArrowBack size={22} />
            </button>

            <div className="ordersMobileTitle">
              {t('title', { defaultValue: 'Orders' })}
            </div>

            <button
              style={{ opacity: '0', visibility: 'hidden' }}
              type="button"
              className="ordersMobileIconBtn"
              aria-label={t('a11y.menu', { defaultValue: 'Menu' })}
            >
              <MdMoreHoriz size={22} />
            </button>
          </div>

          <div className="ordersInner">
            <h1 style={{ marginTop: '100px' }} className="ordersTitle">
              {t('title', { defaultValue: 'Orders' })}
            </h1>

            <div className="ordersSection">
              <div className="ordersSectionLabel">
                {t('createOrder', { defaultValue: 'Create order' })}
              </div>

              <button type="button" className="ordersCreateCard" onClick={onCreateOrder}>
                <div className="ordersCreateText">
                  <div className="ordersCreateTitle">
                    {t('orderPlaylist', { defaultValue: 'Order playlist' })}
                  </div>
                  <div className="ordersCreateSub">
                    {t('playlist', { defaultValue: 'Playlist' })}
                  </div>
                </div>

                <span className="ordersCreatePlus" aria-hidden="true">
                  <MdAdd size={20} />
                </span>
              </button>
            </div>

            {listLoading ? (
              <div style={{ display: 'grid', placeItems: 'center', padding: 30 }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                <div className="ordersSection">
                  <div className="ordersSectionLabel">
                    {t('pendingOrders', { defaultValue: 'Pending orders' })}
                  </div>

                  <div className="ordersGrid">
                    {pending.map((o) => (
                      <button
                        key={o._id}
                        type="button"
                        className="orderCard"
                        onClick={() => onOpenOrder(o)}
                      >
                        <div className="orderCardIcon">
                          <MdMusicNote size={22} />
                        </div>

                        <div className="orderCardBody">
                          <div className="orderCardDate">
                            {formatDate(o.createdAt, locale)}
                          </div>
                          <div className="orderCardStatus">{statusLabel.processing}</div>
                        </div>

                        <div className="orderCardChevron" aria-hidden="true">
                          <MdChevronRight size={22} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ordersSection">
                  <div className="ordersSectionLabel">
                    {t('completedOrders', { defaultValue: 'Completed orders' })}
                  </div>

                  <div className="ordersGrid">
                    {completed.map((o) => (
                      <button
                        key={o._id}
                        type="button"
                        className="orderCard isCompleted"
                        onClick={() => onOpenOrder(o)}
                      >
                        <div className="orderCardIcon">
                          <MdMusicNote size={22} />
                        </div>

                        <div className="orderCardBody">
                          <div className="orderCardDate">
                            {formatDate(o.createdAt, locale)}
                          </div>
                          <div className="orderCardStatus">{statusLabel.delivered}</div>
                        </div>

                        <div className="orderCardChevron" aria-hidden="true">
                          <MdChevronRight size={22} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
